import { useCallback } from 'react';
import {
  DEFAULT_LIFECYCLE_STATE_FILTER,
  type LifecycleStateFilterDto,
} from '@terra/shared-dto-types';
import type {
  FindingSeverityType,
  FindingStatusType,
  SignalCategoryType,
} from '@terra/shared-schemas-orchestrator';
import { createTabScopedStore } from './createTabScopedStore';
import type { FindingsBulkFilter } from '../../clients/tester-client/types';
import type { FindingsGroupByField } from '@terra/shared-contracts-findings';

export type FindingPublishedFilter =
  | 'ALL'
  | 'published'
  | 'unpublished'
  | 'auto-published';
export type FindingHasPreventionFilter = 'ALL' | 'yes' | 'no';

export type FindingsSelectionState =
  | {
      mode: 'ids';
      ids: string[];
    }
  | {
      mode: 'filter';
      filter: FindingsBulkFilter;
      excludedIds: string[];
      totalMatching: number;
    };

export type FindingsTabFilterState = {
  searchTerm: string;
  severityFilter: FindingSeverityType[];
  statusFilters: FindingStatusType[];
  categoryFilter: SignalCategoryType[];
  publishedFilter: FindingPublishedFilter;
  hasPreventionFilter: FindingHasPreventionFilter;
  hasPendingSeverityChangeRequestFilter: boolean;
  createdStartDate: Date | null;
  createdEndDate: Date | null;
  createdSince: Date | null;
  sortModel: Array<{ colId: string; sort: 'asc' | 'desc' }>;
  groupByField: FindingsGroupByField | null;
  groupSortOrder: 'asc' | 'desc' | null;
  lifecycleState: LifecycleStateFilterDto;
  selection: FindingsSelectionState;
};

export const findingsTabFilterResetState: Omit<
  FindingsTabFilterState,
  'groupByField' | 'groupSortOrder' | 'selection'
> = {
  searchTerm: '',
  severityFilter: [],
  statusFilters: [],
  categoryFilter: [],
  publishedFilter: 'ALL',
  hasPreventionFilter: 'ALL',
  hasPendingSeverityChangeRequestFilter: false,
  createdStartDate: null,
  createdEndDate: null,
  createdSince: null,
  sortModel: [{ colId: 'createdAt', sort: 'desc' }],
  lifecycleState: DEFAULT_LIFECYCLE_STATE_FILTER,
};

const findingsTabFilterDefaults: FindingsTabFilterState = {
  ...findingsTabFilterResetState,
  groupByField: 'isPublished',
  groupSortOrder: null,
  selection: { mode: 'ids', ids: [] },
};

const useFindingsTabScopedFilterStoreBase =
  createTabScopedStore<FindingsTabFilterState>(findingsTabFilterDefaults);

export function useTabScopedFindingsFilters() {
  const { filters, update, clearTabScope } =
    useFindingsTabScopedFilterStoreBase();

  const setSearchTerm = useCallback(
    (searchTerm: string) => update({ searchTerm }),
    [update],
  );
  const setSeverityFilter = useCallback(
    (severityFilter: FindingSeverityType[]) =>
      update({ severityFilter: [...severityFilter] }),
    [update],
  );
  const setStatusFilters = useCallback(
    (statusFilters: FindingStatusType[]) =>
      update({ statusFilters: [...statusFilters] }),
    [update],
  );
  const setCategoryFilter = useCallback(
    (categoryFilter: SignalCategoryType[]) =>
      update({ categoryFilter: [...categoryFilter] }),
    [update],
  );
  const setPublishedFilter = useCallback(
    (publishedFilter: FindingPublishedFilter) => update({ publishedFilter }),
    [update],
  );
  const setHasPreventionFilter = useCallback(
    (hasPreventionFilter: FindingHasPreventionFilter) =>
      update({ hasPreventionFilter }),
    [update],
  );
  const setHasPendingSeverityChangeRequestFilter = useCallback(
    (hasPendingSeverityChangeRequestFilter: boolean) =>
      update({ hasPendingSeverityChangeRequestFilter }),
    [update],
  );
  const setCreatedStartDate = useCallback(
    (createdStartDate: Date | null) => update({ createdStartDate }),
    [update],
  );
  const setCreatedEndDate = useCallback(
    (createdEndDate: Date | null) => update({ createdEndDate }),
    [update],
  );
  const setCreatedSince = useCallback(
    (createdSince: Date | null) => update({ createdSince }),
    [update],
  );
  const setSortModel = useCallback(
    (sortModel: Array<{ colId: string; sort: 'asc' | 'desc' }>) =>
      update({
        sortModel: sortModel.map((entry) => ({
          colId: entry.colId,
          sort: entry.sort,
        })),
      }),
    [update],
  );
  const setGroupByField = useCallback(
    (groupByField: FindingsGroupByField | null) =>
      update({ groupByField, groupSortOrder: null }),
    [update],
  );
  const setSortState = useCallback(
    ({
      sortModel,
      groupSortOrder,
    }: {
      sortModel: Array<{ colId: string; sort: 'asc' | 'desc' }>;
      groupSortOrder: 'asc' | 'desc' | null;
    }) =>
      update({
        sortModel: sortModel.map((entry) => ({
          colId: entry.colId,
          sort: entry.sort,
        })),
        groupSortOrder,
      }),
    [update],
  );
  const setLifecycleState = useCallback(
    (lifecycleState: LifecycleStateFilterDto) => update({ lifecycleState }),
    [update],
  );
  const resetFilters = useCallback(
    (kept: Partial<typeof findingsTabFilterResetState> = {}) =>
      update({ ...findingsTabFilterResetState, ...kept }),
    [update],
  );
  const setSelection = useCallback(
    (selection: FindingsSelectionState) => update({ selection }),
    [update],
  );
  const clearSelection = useCallback(() => {
    update({ selection: { mode: 'ids', ids: [] } });
  }, [update]);

  return {
    ...filters,
    setSearchTerm,
    setSeverityFilter,
    setStatusFilters,
    setCategoryFilter,
    setPublishedFilter,
    setHasPreventionFilter,
    setHasPendingSeverityChangeRequestFilter,
    setCreatedStartDate,
    setCreatedEndDate,
    setCreatedSince,
    setSortModel,
    setSortState,
    setGroupByField,
    setLifecycleState,
    resetFilters,
    setSelection,
    clearSelection,
    clearTabScope,
  };
}
